import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from "@angular/common/http";
import { ToastrService } from 'ngx-toastr';

import { TemplateService } from '@service/templates.service';
import { ProjectService } from '@service/project.service';
import { Events } from '@service/events.service';
import { AuthService } from '@service/auth.service';
import { Template } from '@model/Template';
import { TextStructureGeneral } from '@model/TextStructureGeneral';
import { TextStructureFont } from '@model/TextStructureFont';
import { TextStructureCover } from '@model/TextStructureCover';
import { TextStructureIllustration } from '@model/TextStructureIllustration';
import { TextStructurePreTextuals } from '@model/TextStructurePreTextual';
import { TextStructurePosTextuals } from '@model/TextStructurePosTextual';
import { TextStructureTextuals } from '@model/TextStructureTextual';

@Component({
  selector: 'app-template-form',
  templateUrl: './template-form.component.html',
  styleUrls: ['./template-form.component.scss']
})
export class TemplateFormComponent implements OnInit {

  @Input() public projectId: number;
  @Input() public formTemplateVisible: boolean;
  @Input() private nameAffiliation: any;

  public templateForm: FormGroup;
  public templateFormSubmitted: boolean = false;
  public optionTemplateSelected: boolean = false;
  public template_types: any[] =  [];

  constructor(
    private fb: FormBuilder,
    private templateService: TemplateService,
    private events: Events,
    private authService: AuthService,
    private toast: ToastrService,
  ) {}

  ngOnInit() {
    this.template_types = this.templateService.templateTypes;

    this.templateForm = this.fb.group({
      type: ['', [Validators.required]],
    });
  }

  saveTemplate(): void {
    this.templateFormSubmitted = true;

    if (this.templateForm.invalid) {
      return;
    }

    const formData: any = this.templateForm.value;

    this.templateService.store({
      name_template: formData.nameTemplate,
      type: formData.type,
      project_id: this.projectId,
    }).subscribe((res: any) => {
      const {template, ...rest} = res;
      if (!ProjectService.getTemplate()) {
        ProjectService.setTemplate(template);
      }
      this.templateFormSubmitted = false;
      this.templateForm.patchValue({
        type: ''
      });
      this.saveDefaultData(template, this.templateService.templateTypes.find(t => t.id === formData.type).name);
      this.events.broadcast('templateAdded', { template, rest, type: formData.type });
      this.formTemplateVisible = false;
      this.hideNameTemplateValueField();
    });
  }

  onTypeChange(e): void {
    const selectedValue = e.target.value;
    if(selectedValue) {
      this.addNameTemplateValueField();
      this.showNameTemplateValueField();
      this.setNameTemplateValue(selectedValue);
    }
  }

  setNameTemplateValue(type: number) {
    this.templateForm.patchValue({
      nameTemplate: 'MODELO ' +this.templateService.templateTypes.find(el=> el.id === type).name+' '+ this.nameAffiliation
    });
  }

  addNameTemplateValueField() {
    this.templateForm.addControl('nameTemplate', new FormControl('', Validators.required));
  }

  showNameTemplateValueField(): void {
    this.optionTemplateSelected = true;
  }

  hideNameTemplateValueField(): void {
    this.optionTemplateSelected = false;
  }


  private saveDefaultData(template: Template, templateType: string) {
    // mount general default data
    const general: TextStructureGeneral = {
      affiliation: this.authService.user.student_institution.info.affiliation.name,
      affiliation_address: {
        uf: this.authService.user.student_institution.info.affiliation.address.uf,
        city: {
          co_municipio: this.authService.user.student_institution.info.affiliation.address.city.co_municipio,
          ds_municipio: this.authService.user.student_institution.info.affiliation.address.city.ds_municipio
        }
      },
      template_id: template.id,
      paper_size: 'A4'
    } as TextStructureGeneral;
    this.templateService.storeGeneralStructure(general)
      .subscribe((textStructureGeneral: TextStructureGeneral) => {}
    , this.errorHandler);

    const templateTypesDefaultValues = {
      'PROJETO|ARTIGO|RELATÓRIO|MODELO DE NEGÓCIO|REVISÃO SISTEMÁTICA|REVISÃO INTEGRATIVA|TRABALHO - SALA DE AULA': {
        text_structure_pre_textuals: {
          show_resume: true,
          show_abstract: true
        },
        text_structure_textuals: {
          show_title: true,
          show_authors_names: true
        },
        text_structure_pos_textuals: {
          show_reference: true
        }
      },
      'TCC|MONOGRAFIA|DISSERTAÇÃO|TESE': {
        font: {
          font_sizes_and_space_lines: { font_sizes:[12], space_lines: ['1,5'] },
          space_between_titles: { space_before:[1], space_after: [0] },
          epigraph_position: { position: ['bottom'] },
          show_number_page: true
        },
        illustrations: {
          space_between_images: { space_before:[0], space_after: [1] },
          space_between_tables: { space_before:[0], space_after: [1] }
        },
        text_structure_pre_textuals: {
          show_capa: true,
          show_thanks: true,
          show_list_illustration: true,
          show_cover_page: true,
          show_epigraph: true,
          show_list_abbreviations: true,
          show_approval_sheet: true,
          show_resume: true,
          show_simbol_list: true,
          show_frame_list: true,
          show_diagram_list: true,
          show_graphic_list: true,
          show_dedication: true,
          show_abstract: true,
          show_summary: true,
          show_catalog: true,
          show_table_list: true
        },
        text_structure_textuals: {
          separated_pages: true
        },
        text_structure_pos_textuals: {
          show_reference: true,
          show_all_appendices: true,
          show_all_atachments: true
        }
      }
    };
    const hasTemplateTypesDefaultValues = Object.keys(templateTypesDefaultValues).find(value => value.includes(templateType));

    // mount font default data
    const font: TextStructureFont = {
      template_id: template.id,
      font_default: 'Times New Roman',
      font_sizes_and_space_lines: { font_sizes:[12], space_lines: ['Simples'] },
      font_sizes_and_space_lines_quotes: { font_sizes: [10], space_lines: ['Simples'] },
      space_between_titles: { space_before:[0], space_after: [0] },
      space_between_lines_resume: { space_lines: ['Simples'] },
      retreat_direct_long_quotes: { retreat: ['4.0'] },
      retreat_paragraph: { retreat: ['1.25'] },
      epigraph_position: { position: ['middle'] },
      number_page_position: { position: ['header'] },
      show_number_page: false,
      page_margins: { left: ['3.0'], top: ['3.0'], right: ['2.0'], bottom: ['2.0'] },
      title_resume_italic: false,
      document_language: 'portuguese'
    } as TextStructureFont;
    const formatting = hasTemplateTypesDefaultValues && templateTypesDefaultValues[hasTemplateTypesDefaultValues].font
      ? Object.assign({}, font, templateTypesDefaultValues[hasTemplateTypesDefaultValues].font) : font;
    this.templateService.storeFontStructure(formatting)
      .subscribe((textStructureFont: TextStructureFont) => {}
    , this.errorHandler);

    if ('TCC|MONOGRAFIA|DISSERTAÇÃO|TESE'.includes(templateType)) {
      // mount cover default data
      const payload = new FormData();
      payload.append('template_id', JSON.stringify(template.id));
      payload.append('url_image', '');
      payload.append('using_logo', 'false');
      payload.append('using_departament', 'true');
      payload.append('alignment_logo', 'center');
      payload.append('using_cursor', 'true');
      payload.append('use_escritha_logo', 'false');
      payload.append('use_header_logo', 'false');
      payload.append('does_not_show_ministerio_da_educacao', 'false');
      this.templateService.storeCoverStructure(payload)
        .subscribe(events => {}
      , this.errorHandler);
    }

    // mount illustration default data
    const illustrations: TextStructureIllustration = {
      template_id: template.id,
      position_title_table: 'top',
      position_title_image: 'top',
      space_between_images: { space_before:[0], space_after: [0] },
      space_between_tables: { space_before:[0], space_after: [0] },
      alignment_illustration: 'center',
      alignment_title: 'center',
      alignment_fonts: 'center'
    } as TextStructureIllustration;
    const structureIllustration = hasTemplateTypesDefaultValues && templateTypesDefaultValues[hasTemplateTypesDefaultValues].illustrations
      ? Object.assign({}, illustrations, templateTypesDefaultValues[hasTemplateTypesDefaultValues].illustrations) : illustrations;
    this.templateService.storeIllustrationStructure(structureIllustration)
      .subscribe((textStructureIllustration: TextStructureIllustration) => {}
    , this.errorHandler);

    // mount text_structure_pre_textuals default data
    const text_structure_pre_textuals: TextStructurePreTextuals = {
      template_id: template.id,
      show_capa: false,
      show_thanks: false,
      show_list_illustration: false,
      show_cover_page: false,
      show_epigraph: false,
      show_list_abbreviations: false,
      show_approval_sheet: false,
      show_resume: false,
      show_simbol_list: false,
      show_frame_list: false,
      show_diagram_list: false,
      show_graphic_list: false,
      show_dedication: false,
      show_abstract: false,
      show_summary: false,
      show_catalog: false,
      show_french_resume: false,
      show_spanish_resume: false,
      show_italian_resume: false,
      show_table_list: false
    } as TextStructurePreTextuals;
    const structurePreTextuals = hasTemplateTypesDefaultValues && templateTypesDefaultValues[hasTemplateTypesDefaultValues].text_structure_pre_textuals
      ? Object.assign({}, text_structure_pre_textuals, templateTypesDefaultValues[hasTemplateTypesDefaultValues].text_structure_pre_textuals) : text_structure_pre_textuals;
    this.templateService.storePreTextualStructure(structurePreTextuals)
      .subscribe((textStructurePreTextual: TextStructurePreTextuals) => {}
    , this.errorHandler);

    // mount text_structure_textuals default data
    const text_structure_textuals: TextStructureTextuals = {
      template_id: template.id,
      separated_pages: false,
      show_title: false,
      show_authors_names: false
    } as TextStructureTextuals;
    const structureTextuals = hasTemplateTypesDefaultValues && templateTypesDefaultValues[hasTemplateTypesDefaultValues].text_structure_textuals
      ? Object.assign({}, text_structure_textuals, templateTypesDefaultValues[hasTemplateTypesDefaultValues].text_structure_textuals) : text_structure_textuals;
    this.templateService.storeTextualStructure(structureTextuals)
      .subscribe((textStructurePreTextual: TextStructureTextuals) => {}
    , this.errorHandler);

    // mount text_structure_pos_textuals default data
    const text_structure_pos_textuals: TextStructurePosTextuals = {
      template_id: template.id,
      show_reference: false,
      show_glossary: false,
      show_all_appendices: false,
      show_all_atachments: false,
      show_index: false,
      show_identification: false
    } as TextStructurePosTextuals;
    const structurePosTextuals = hasTemplateTypesDefaultValues && templateTypesDefaultValues[hasTemplateTypesDefaultValues].text_structure_pos_textuals
      ? Object.assign({}, text_structure_pos_textuals, templateTypesDefaultValues[hasTemplateTypesDefaultValues].text_structure_pos_textuals) : text_structure_pos_textuals;
    this.templateService.storePosTextualStructure(structurePosTextuals)
      .subscribe((textStructurePreTextual: TextStructurePosTextuals) => {}
    , this.errorHandler);
  }

  private errorHandler = (e: HttpErrorResponse) => {
    for (const key in e.error.errors) {
      this.toast.error(e.error.errors[key]);
    }
  }
}
